import "moment/locale/pt-br";
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { AiOutlineInfoCircle, AiOutlineSearch } from "react-icons/ai";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useTour } from "hooks/useTour";

import Chart from "components/MI/minha-conta/Chart";
import CodeVerification from "components/MI/minha-conta/CodeVerification";
import ContainedButton from "components/layout/form/containedButton";
import Form from "components/MI/minha-conta/Form";
import Loading from "components/layout/loading";
import ModalBill from "components/MI/minha-conta/ModalBill";
import ModalInfo from "components/MI/minha-conta/ModalInfo";
import ModalValidationContacts from "components/MI/minha-conta/ModalValidationContacts";
import Navbar from "components/MI/Navbar";
import NotFoundBills from "components/MI/minha-conta/NotFoundBills";
import TourBalloon from "components/NewTour/TourBallon";
import UserMenu from "components/layout/userMenuNew";
import api from "services/api";

import {
  Body,
  Button,
  Container,
  Content,
  ContentCode,
  ContentInfos,
  ContentLeft,
  ContentResult,
  ContentRight,
  ContentSubtitle,
  ContentTexts,
  ContentTitle,
  ContentWrap,
  Header,
  HeaderInfos,
  HeaderSubtitle,
  HeaderTitle,
} from "./styles";

const Consumo = (): JSX.Element => {
  moment.locale("pt-br");
  const [status, setStatus] = useState("");
  const [billsFormatted, setBillsFormatted] = useState([]);
  const [firstBill, setFirstBill] = useState<any>();
  const [code, setCode] = useState<any>({
    value: "",
    error: "",
  });
  const [openModalInfo, setOpenModalInfo] = useState(false);
  const [openModalBill, setOpenModalBill] = useState(false);
  const [openModalValidation, setOpenModalValidation] = useState(false);
  const [statusCalc, setStatusCalc] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [months, setMonths] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [protocol, setProtocol] = useState("");
  const selectedUnit = useSelector(
    // @ts-ignore
    (state) => state.selectUnitReducer.selectedUnit
  );

  const { showTour, setTourType, setSteps, tourStep, tourType } = useTour();

  useEffect(() => {
    setTourType("consumo");
    setSteps(3);
  }, [showTour, setTourType, setSteps]);

  useEffect(() => {
    (async () => {
      setStatus("loading");
      await getBills();
      await requestGetUserContacts();
      setStatus("ready");
    })();

    return () => {
      const codeValue = localStorage.getItem("codeValue");

      if (codeValue?.length === 8) {
        const measurement = codeValue;
        const body = {
          attachments: [],
          comments: "",
          months: "",
          measurement,
          completed: false,
        };

        submitContestations(body);
      }
    };
  }, []);

  const getBills = async (): Promise<void> => {
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { instanceId } = selectedUnit;

    const endDate = moment()
      .add(1, "months")
      .startOf("month")
      .format("YYYY-MM-DD");
    const beginDate = moment(endDate)
      .subtract(2, "years")
      .endOf("month")
      .format("YYYY-MM-DD");

    const params = {
      document,
      "unit-id": instanceId,
      "begin-date": beginDate,
      "end-date": endDate,
    };

    const response = await api.get("industrial/bill/v1/bills/consumption", {
      params,
      headers,
    });

    const { data } = response;

    if (data?.length) {
      setFirstBill(data[0]);
      const firstBillDate = data[0]?.measurementDate;
      const newMonths = data
        ?.map((item) => {
          return {
            value: moment(
              item?.measurementDate?.replaceAll("-", "/"),
              "YYYY-MM-DD"
            ).format("MM/YYYY"),
            label: moment(
              item?.measurementDate?.replaceAll("-", "/"),
              "YYYY-MM-DD"
            ).format("MMMM/YYYY"),
          };
        })
        .splice(0, 13);
      setMonths(newMonths);
      const newData = data
        ?.map((item, index) => {
          let monthValueColor = "#000fff";

          if (index === 0) {
            monthValueColor = "#00f00f";
          } else {
            const firstBillMonth = moment(firstBillDate).format("MM");
            const currentBillMonth = moment(item?.measurementDate).format("MM");
            if (firstBillMonth === currentBillMonth) {
              monthValueColor = "#00ffff";
            }
          }

          return {
            month: moment(
              item?.measurementDate?.replaceAll("-", "/"),
              "YYYY-MM-DD"
            ).format(""),
            monthValue: item?.kgConsumption.replace(",", "."),
            monthValueColor,
          };
        })
        .splice(0, 13)
        .reverse();
      setBillsFormatted(newData);
    } else {
      setBillsFormatted([]);
    }
  };

  const toggleModalInfo = (): void => {
    setOpenModalInfo((prevState) => !prevState);
  };

  const toggleModalBill = (): void => {
    setOpenModalBill((prevState) => !prevState);
  };

  const toggleModalValidation = (): void => {
    setOpenModalValidation((prevState) => !prevState);
  };

  const onChangeCode = (value): void => {
    localStorage.setItem("codeValue", value);
    setCode({
      value,
      error: "",
    });
  };

  const calculateKg = (): any => {
    const { value } = code;

    if (value?.length < 8) {
      return setCode((prevState) => {
        localStorage.setItem("codeValue", prevState?.value);
        return {
          ...prevState,
          error: "É necessário incluir todos os dígitos do medidor",
        };
      });
    }

    const int = parseInt(value?.split("").splice(0, 5).join(""), 10);
    const decimals = value?.split("").splice(5, 7).join("");
    const newValue = `${int}.${decimals}` as any;
    const newFirstBill = firstBill?.finalMeasurement.replace(",", ".");
    const calc = newValue - newFirstBill;
    let statusTemp;

    if (calc >= 0) {
      statusTemp = "success";
      setShowForm(false);
    } else {
      statusTemp = "error";
      setShowForm(true);
    }
    localStorage.setItem("statusCalc", statusTemp);
    return setStatusCalc(statusTemp);
  };

  const openForm = (): void => {
    setShowForm(true);
  };

  const toBase64 = (file: any): Promise<void> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () =>
        // @ts-ignore
        resolve(reader.result.replace(/data[:].*?\/.*?;base64,/, ""));
      reader.onerror = (error) => reject(error);
    });

  const handleSubmit = async (
    { months, files, description }: any,
    { setSubmitting, resetForm }: any
  ): Promise<void> => {
    setSubmitting(true);
    const measurement = code?.value;
    let error = "";

    const body = {
      measurement,
      completed: true,
      attachments: [],
      comments: "",
      months: "",
    };

    if (months?.length > 1) {
      body.months = months?.join(",");
    } else {
      body.months = months?.[0];
    }

    if (files?.length) {
      const filesPromise = files?.map(async (item) => {
        const base64 = await toBase64(item);
        return {
          filename: item?.name,
          size: item?.size,
          data: base64,
        };
      });
      files = await Promise.all(filesPromise).then((values) => values);
      const size = files?.reduce((acc, item) => acc + item.size, 0);

      if (size > 5000000) {
        error = "O tamanho máximo dos arquivos é de 5MB";
      }
      body.attachments = files;
    }

    if (description) {
      body.comments = description;
    }

    try {
      if (!error) {
        await submitContestations(body);
        localStorage.setItem("codeValue", "");
        setCode({
          value: "",
          errors: "",
        });
        localStorage.setItem("statusCalc", "");
        setStatusCalc("");
        setShowForm(false);
        resetForm();
        setSubmitting(false);
      } else {
        throw new Error(error);
      }
    } catch (error) {
      setSubmitting(false);
      toast.error(error?.message);
    }
  };

  const submitContestations = async (data): Promise<void> => {
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const { instanceId } = selectedUnit;
    const siteId = localStorage.getItem("@UltragazEmpresarial:customerSiteId");
    const contactIdSf = localStorage.getItem(
      "@UltragazEmpresarial:contactIdSf"
    );
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };

    const body = {
      ...data,
      document,
      unitInstanceId: instanceId,
      siteId,
      contactIdSf,
    };
    let success;

    try {
      const result = await api.post("industrial/case/v1/contestations", body, {
        headers,
      });
      const { status } = result;

      if (body?.completed && status === 201) {
        const id = result?.headers?.location?.split("contestations/")[1];
        const { data: dataId } = await api.get(
          `industrial/case/v1/contestations/${id}`,
          { headers }
        );
        setProtocol(dataId?.caseNumber);
        toggleModalValidation();
      }

      success = true;
    } catch (error) {
      console.error(error);
      success = false;
    }

    return success;
  };

  const requestGetUserContacts = useCallback(async () => {
    const headers = {
      access_token: localStorage.getItem("@UltragazEmpresarial:accessToken"),
    };
    const response = await api.get(
      `industrial/customer/v1/customers/me/contacts`,
      {
        headers,
      }
    );
    const { data } = response;
    setContacts(data?.filter((item) => item?.isValidated));
  }, []);

  return (
    <>
      <Navbar selected="consumo" openSubMenu>
        <UserMenu intro />
        <Container>
          <Header>
            <HeaderInfos>
              <HeaderTitle>Consumo</HeaderTitle>
              <HeaderSubtitle>
                Aqui você encontra todos consumos dos últimos 12 meses!
              </HeaderSubtitle>
            </HeaderInfos>
            <ContentWrap
              showTour={tourStep === 3 && tourType === "consumo" && showTour}
            >
              <Button
                target="_blank"
                to={{
                  pathname: "https://www.ultragaz.com.br/entenda-sua-conta/",
                }}
              >
                Entenda a conta
              </Button>
              {showTour && tourStep === 3 && tourType === "consumo" && (
                <TourBalloon
                  title="Entenda sua conta"
                  content="Saiba como é calculada sua conta de gás"
                  position={{
                    left: "-120px",
                    top: "30px",
                    arrowPosition: "top",
                    arrowTop: "-27%",
                    maxWidth: 320,
                  }}
                />
              )}
            </ContentWrap>
          </Header>
          <Body>
            {billsFormatted?.length > 0 ? (
              <Content>
                <ContentWrap
                  showTour={
                    tourStep === 1 && tourType === "consumo" && showTour
                  }
                >
                  <ContentLeft>
                    <ContentTitle align="center">
                      Histórico de consumo - Volume (Kg)
                    </ContentTitle>
                    <Chart data={billsFormatted} />
                  </ContentLeft>
                  {showTour && tourStep === 1 && tourType === "consumo" && (
                    <TourBalloon
                      title="Consumo"
                      content="Encontre aqui seu histórico de consumo (em Kg) nos últimos 12 meses!"
                      position={{
                        left: "446px",
                        top: "0",
                        arrowPosition: "left",
                        arrowLeft: "-10%",
                        maxWidth: 320,
                        boxShadow: true,
                      }}
                    />
                  )}
                </ContentWrap>
                <ContentWrap
                  showTour={
                    tourStep === 2 && tourType === "consumo" && showTour
                  }
                >
                  <ContentRight>
                    <ContentTitle align="start">Ainda com dúvida?</ContentTitle>
                    <ContentInfos>
                      <ContentTexts>
                        <ContentSubtitle>
                          Inclua aqui a leitura atual do seu medidor,
                          considerando os zeros.
                        </ContentSubtitle>
                      </ContentTexts>
                      <AiOutlineInfoCircle onClick={toggleModalInfo} />
                    </ContentInfos>
                    <ContentCode>
                      <CodeVerification
                        onChange={(e) => onChangeCode(e)}
                        value={code?.value}
                        error={code?.error}
                        calculateKg={calculateKg}
                      />
                    </ContentCode>
                    <ContentInfos>
                      <ContentTexts>
                        <ContentSubtitle>
                          *Se a leitura atual do medidor for menor do que a
                          última leitura registrada no seu boleto, pode haver
                          divergência.
                        </ContentSubtitle>
                        <ContentSubtitle>
                          {`Sua última leitura foi de: `}
                          <strong>{firstBill?.finalMeasurement}</strong>
                        </ContentSubtitle>
                      </ContentTexts>
                      <AiOutlineInfoCircle onClick={toggleModalBill} />
                    </ContentInfos>
                    {statusCalc === "success" && (
                      <div>
                        <ContentResult success>
                          <AiOutlineSearch />
                          <p>
                            A leitura atual é maior ou igual à leitura anterior,
                            o que indica que a leitura foi feita corretamente e
                            que seu medidor está funcionando normalmente.
                          </p>
                        </ContentResult>
                        {!showForm && (
                          <ContainedButton
                            name="solicitar-analise"
                            id="solicitar-analise"
                            onClick={openForm}
                          >
                            Ainda quero solicitar uma análise mais profunda
                          </ContainedButton>
                        )}
                      </div>
                    )}
                    {statusCalc === "error" && (
                      <ContentResult success={false}>
                        <AiOutlineSearch />
                        <p>
                          Por favor, preencha as informações abaixo para que
                          possamos avaliar o que aconteceu.
                        </p>
                      </ContentResult>
                    )}
                    {showForm && (
                      <div>
                        <ContentTitle align="start">
                          Deseja solicitar uma análise mais profunda por parte
                          da Ultragaz?
                        </ContentTitle>
                        <Form handleSubmit={handleSubmit} months={months} />
                      </div>
                    )}
                  </ContentRight>
                  {showTour && tourStep === 2 && tourType === "consumo" && (
                    <TourBalloon
                      title="Contestação"
                      content="Informe aqui a leitura atual do seu medidor, caso deseje solicitar uma análise de leitura."
                      position={{
                        left: "442px",
                        top: "0",
                        arrowPosition: "left",
                        arrowLeft: "-10%",
                        maxWidth: 320,
                        boxShadow: true,
                      }}
                    />
                  )}
                </ContentWrap>
              </Content>
            ) : (
              <NotFoundBills />
            )}
          </Body>
        </Container>
      </Navbar>

      <ModalInfo open={openModalInfo} toggleModal={toggleModalInfo} />
      <ModalBill open={openModalBill} toggleModal={toggleModalBill} />
      <ModalValidationContacts
        open={openModalValidation}
        toggleModal={toggleModalValidation}
        contacts={contacts}
        protocol={protocol}
        getContacts={requestGetUserContacts}
      />
      {status === "loading" && <Loading />}
    </>
  );
};

export default Consumo;
